// Finished Goods Inventory — saldo barang jadi. Phase 1D-δ-1.
// Pola daftar seragam (28 Sep 2026; `docs/POLA_DAFTAR.md`): mode SERVER penuh, keadaan di alamat
// halaman, saringan bawaan = gudang sendiri (Admin Gudang).
//
// ⛔ DULU kotak "Cari nomor WO" disaring backend SESUDAH satu halaman diambil: WO yang cocok di
//    halaman lain tidak pernah ketemu, dan "Total" tetap menghitung semua baris. Kini nomor WO
//    dicari di server atas SELURUH saldo.
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Boxes, RefreshCw, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import { listFGBalance, apiErrorMessage } from "../../lib/inventoryApi";
import { api } from "../../lib/api";
import { formatQty } from "../../lib/format";
import Hint from "../../components/etams/Hint";
import { useAuth } from "../../contexts/AuthContext";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";

const LABEL_PRODUK = { ATASAN: "Atasan", CELANA: "Celana" };
//: Cermin `URUT_SAH_FG_SALDO` di backend/services/finished_goods_service.py.
const URUT_SAH = ["updated_at", "qty_available", "product_type"];

const KOLOM_CSV = [
  { kunci: "wo", judul: "WO", ambil: (it) => it.wo?.wo_number || "" },
  { kunci: "store", judul: "Gudang", ambil: (it) => it.store?.name || "" },
  { kunci: "product_type", judul: "Produk", ambil: (it) => LABEL_PRODUK[it.product_type] || "" },
  { kunci: "qty_available", judul: "Jumlah Tersedia" },
];

export default function FinishedGoods() {
  const { roleCode, profile } = useAuth();
  const gudangSaya = (roleCode === "ADMIN_GUDANG" && profile?.store_id) || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  // Inventaris B13: saldo barang jadi PER GUDANG.
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const r = await api.get("/stores"); setStores(Array.isArray(r.data) ? r.data : []); }
      catch (e) { toast.error(apiErrorMessage(e, "Daftar gudang gagal dimuat.")); }
    })();
  }, []);

  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "updated_at", arah: "desc" },
    saringan: {
      produk: { label: "Produk", param: "product_type", tampil: (v) => LABEL_PRODUK[v] || v },
      gudang: { label: "Gudang", param: "store_id", bawaan: gudangSaya,
                tampil: (v) => stores.find(s => s.id === v)?.name || "…" },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const bangunParams = useCallback((o = {}) => ({
    ...paramSaringan, ...(cari && { q: cari }), limit, offset, sort, arah, ...o,
  }), [paramSaringan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await listFGBalance(bangunParams({ offset: off, limit: n }))).data, [bangunParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listFGBalance(bangunParams());
      setItems(r.data.items || []);
      setTotal(r.data.total ?? 0);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat saldo barang jadi."));
    } finally {
      setLoading(false);
    }
  }, [bangunParams]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="fg-balance-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Boxes className="h-6 w-6" /> Inventori Barang Jadi
            <Hint>Saldo per WO, gudang, dan jenis produk. Diperbarui otomatis dari barang masuk produksi, transfer, dan adjustment.</Hint>
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-1" /> Muat ulang
          </Button>
          <Link to="/inventory/finished-goods/transfers">
            <Button size="sm" variant="outline">
              <ArrowLeftRight className="h-4 w-4 mr-1" /> Transfer
            </Button>
          </Link>
        </div>
      </div>

      <PalangDaftar daftar={daftar} placeholder="Cari nomor WO" testId="fg-search"
                    ekspor={{ nama: "saldo_barang_jadi", kolom: KOLOM_CSV, ambilHalaman }}>
        <select value={daftar.nilaiSaringan.produk} onChange={(e) => daftar.setSaringan("produk", e.target.value)}
                className="h-9 rounded border px-2 text-sm" data-testid="fg-pt-filter" aria-label="Saring produk">
          <option value="">Semua Produk</option>
          <option value="ATASAN">Atasan</option>
          <option value="CELANA">Celana</option>
        </select>
        <select value={daftar.nilaiSaringan.gudang} onChange={(e) => daftar.setSaringan("gudang", e.target.value)}
                className="h-9 rounded border px-2 text-sm" data-testid="fg-store-filter" aria-label="Saring gudang">
          <option value="">Semua Gudang</option>
          {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </PalangDaftar>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium">WO</th>
              <th className="px-4 py-3 font-medium">Gudang</th>
              <ThUrut daftar={daftar} kunci="product_type" className="px-4 py-3 font-medium">Produk</ThUrut>
              <ThUrut daftar={daftar} kunci="qty_available" className="px-4 py-3 font-medium" align="right">Jumlah Tersedia</ThUrut>
            </tr>
          </thead>
          <tbody>
            {loading && daftar.tampil.length === 0 && (
              <tr><td colSpan="4" className="p-8 text-center text-muted-foreground">Memuat...</td></tr>
            )}
            {!loading && daftar.tampil.length === 0 && (
              <tr><td colSpan="4" className="p-12 text-center text-muted-foreground text-sm" data-testid="fg-kosong">
                {daftar.cari.trim() || daftar.saringanAktif.length
                  ? "Tidak ada saldo yang cocok dengan pencarian atau saringan ini."
                  : "Belum ada saldo barang jadi."}
              </td></tr>
            )}
            {daftar.tampil.map((it) => (
              <tr key={it.id} className="border-b hover:bg-muted/50">
                <td className="px-4 py-3 font-mono text-xs">{it.wo?.wo_number || "—"}</td>
                <td className="px-4 py-3">{it.store?.name || "-"}</td>
                <td className="px-4 py-3">
                  <Badge variant="outline">{LABEL_PRODUK[it.product_type] || "—"}</Badge>
                </td>
                <td className="px-4 py-3 text-right font-semibold">{formatQty(it.qty_available)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Paginasi daftar={daftar} testId="fg-paginasi" />
      </Card>
    </div>
  );
}
